import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Send as SendIcon,
  Psychology as BotIcon,
  Person as UserIcon,
  Source as SourceIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import PolicyUpload from './PolicyUpload';

const EnhancedChatInterface = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: 'مرحباً بك في نظام الموارد البشرية! كيف يمكنني مساعدتك؟\n\nWelcome to HR System! How can I help you?',
      timestamp: new Date(),
      kbUsed: false,
      citations: []
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedCitations, setExpandedCitations] = useState({});
  const messagesEndRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const toggleCitations = (messageId) => {
    setExpandedCitations(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: data.response,
        timestamp: new Date(),
        kbUsed: data.kb_used || false,
        citations: data.citations || []
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (err) {
      setError('فشل الاتصال بالخادم - Failed to connect to server');
      console.error('Chat error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Policy Upload Section */}
      <PolicyUpload apiUrl={apiUrl} />

      {/* Chat Messages Area */}
      <Paper 
        elevation={3} 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Messages Container */}
        <Box 
          sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            p: 2,
            bgcolor: 'background.default'
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                mb: 2
              }}
            >
              <Box
                sx={{
                  maxWidth: '70%',
                  display: 'flex',
                  flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
                  gap: 1
                }}
              >
                {/* Avatar */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: message.type === 'user' ? 'primary.main' : 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}
                >
                  {message.type === 'user' ? <UserIcon /> : <BotIcon />}
                </Box>

                {/* Message Content */}
                <Box sx={{ flex: 1 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: message.type === 'user' ? 'primary.light' : 'white',
                      borderRadius: 2
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        textAlign: 'right',
                        direction: 'rtl'
                      }}
                    >
                      {message.content}
                    </Typography>

                    {/* Knowledge Base Indicator */}
                    {message.type === 'bot' && message.kbUsed && (
                      <Box sx={{ mt: 1 }}>
                        <Chip 
                          label="استخدم السياسات المحملة / Used Uploaded Policies"
                          size="small"
                          color="success"
                          icon={<SourceIcon />}
                        />
                      </Box>
                    )}

                    {/* Citations */}
                    {message.type === 'bot' && message.citations && message.citations.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={`${message.citations.length} مصادر / Sources`}
                          size="small"
                          icon={<SourceIcon />}
                          onClick={() => toggleCitations(message.id)}
                          clickable
                          sx={{ cursor: 'pointer' }}
                        />
                        <Collapse in={expandedCitations[message.id]} timeout="auto" unmountOnExit>
                          <List dense sx={{ mt: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                            {message.citations.map((citation, idx) => (
                              <ListItem key={idx}>
                                <ListItemText
                                  primary={`${citation.source} - جزء ${citation.chunk_id}`}
                                  secondary={citation.text_preview}
                                  sx={{ textAlign: 'right', direction: 'rtl' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </Box>
                    )}

                    {/* Timestamp */}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block',
                        mt: 0.5,
                        color: 'text.secondary',
                        textAlign: message.type === 'user' ? 'right' : 'left'
                      }}
                    >
                      {message.timestamp.toLocaleTimeString('ar-SA')}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </Box>
          ))}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError('')}
            sx={{ m: 2 }}
          >
            {error}
          </Alert>
        )}

        {/* Input Area */}
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="اكتب سؤالك هنا... / Type your question here..."
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  direction: 'rtl',
                  textAlign: 'right'
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={sendMessage}
              disabled={isLoading || !inputMessage.trim()}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark'
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground'
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>

          {/* Quick Actions */}
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="ما هي شروط الترقية؟"
              size="small"
              onClick={() => setInputMessage('ما هي شروط الترقية؟')}
              clickable
            />
            <Chip
              label="كم مقدار بدل الاتصالات؟"
              size="small"
              onClick={() => setInputMessage('كم مقدار بدل الاتصالات؟')}
              clickable
            />
            <Chip
              label="ما هي معايير المفاضلة؟"
              size="small"
              onClick={() => setInputMessage('ما هي معايير المفاضلة بين المتقدمين؟')}
              clickable
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EnhancedChatInterface;
